import { useTranslation } from "react-i18next";
import type { ComponentType, ReactNode } from "react";

import { cn } from "@/utils/cn";

type AlertPillTone = "info" | "warning" | "danger";

interface ToneClasses {
  /** Left edge rail — the instant "this is an alert, and of what severity" read. */
  rail: string;
  /** Tone wash fading out to the plain surface, so the pill is tinted but stays legible. */
  surface: string;
  ring: string;
  iconWrap: string;
  /** Soft halo around the icon chip; gives weight without resorting to motion. */
  halo: string;
  /** Colour is not an accessible channel on its own — screen readers get this instead. */
}

const TONE_CLASSES: Record<AlertPillTone, ToneClasses> = {
  info: {
    rail: "bg-info",
    surface: "from-info-muted to-surface",
    ring: "ring-info/25",
    iconWrap: "bg-info text-info-foreground",
    halo: "ring-info/15",
  },
  warning: {
    rail: "bg-warning",
    surface: "from-warning-muted to-surface",
    ring: "ring-warning/25",
    iconWrap: "bg-warning text-warning-foreground",
    halo: "ring-warning/15",
  },
  danger: {
    rail: "bg-danger",
    surface: "from-danger-muted to-surface",
    ring: "ring-danger/25",
    iconWrap: "bg-danger text-danger-foreground",
    halo: "ring-danger/15",
  },
};

/** Screen-reader tone label — translated, so kept out of the static class map. */
const buildToneSrLabel = (
  t: (key: string) => string,
): Record<AlertPillTone, string> => ({
  info: t("ui.note"),
  warning: t("ui.warning"),
  danger: t("ui.action_needed"),
});

interface AlertPillProps {
  tone: AlertPillTone;
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  title: string;
  description: string;
  actions?: ReactNode;
  className?: React.HTMLAttributes<HTMLElement>["className"];
}

/**
 * Canonical status pill for the dashboard banner's alert slot.
 *
 * Sits *on* the pastel hero rather than floating over the page, so it stays a
 * light inset card — no toast-weight shadow and no backdrop blur, since there is
 * nothing meaningful behind it to blur. Severity is carried by three redundant
 * channels (rail, solid icon chip, tone wash) plus an `sr-only` label, never by
 * colour alone. The action row wraps to its own line before it will squeeze the
 * message.
 */
export const AlertPill = ({
  tone,
  icon: Icon,
  title,
  description,
  actions,
  className,
}: AlertPillProps) => {
  const { t } = useTranslation("my-space");
  const toneClasses = TONE_CLASSES[tone];

  return (
    <div
      role="status"
      className={cn(
        "animate-rise-in relative flex w-full flex-wrap items-center gap-x-2.5 gap-y-1.5 overflow-hidden rounded-xl border border-border/60 bg-linear-to-r py-1.5 pe-2 ps-2.5 shadow-xs ring-1 sm:gap-x-3 sm:py-2 sm:pe-2.5 sm:ps-3.5",
        toneClasses.surface,
        toneClasses.ring,
        className,
      )}
    >
      <span aria-hidden className={cn("absolute inset-y-0 start-0 w-1", toneClasses.rail)} />

      <div
        className={cn(
          "flex size-7 shrink-0 items-center justify-center rounded-lg shadow-xs ring-2 sm:size-8",
          toneClasses.iconWrap,
          toneClasses.halo,
        )}
      >
        <Icon className="size-3.5 sm:size-4" aria-hidden />
      </div>

      {/* `basis-*` is the wrap threshold: the action row drops to its own line rather
          than compressing the message below a readable width. Lower on mobile, where
          160px would leave the icon and text fighting over a ~300px card. */}
      <div className="flex min-w-0 flex-1 basis-32 flex-col gap-0.5 leading-tight sm:basis-40">
        <span className="text-[13px] leading-snug font-semibold text-foreground">
          <span className="sr-only">{buildToneSrLabel(t)[tone]}: </span>
          {title}
        </span>
        {/* Clamped, not truncated — the description names the actual problem, so
            losing it to a single ellipsised line defeats the alert. */}
        <span className="line-clamp-2 text-[11px] leading-snug text-muted-foreground">
          {description}
        </span>
      </div>

      {actions && (
        <div className="flex shrink-0 items-center gap-1.5 max-sm:w-full">{actions}</div>
      )}
    </div>
  );
};
